import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { trackFormStart, trackFormComplete, trackContactFormSubmission } from '@/utils/metaPixel';
import { createKommoLead } from '@/utils/leadSubmission';
import { pushDataLayerEvent } from '@/utils/gtm';
import { useTranslation } from 'react-i18next';
import { useLang } from '@/hooks/useLang';
import { buildLangPath } from '@/utils/routeBuilders';

export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

const defaultFormData: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
};

interface ContactFormProps {
  /** Optional title above the form */
  title?: string;
  /** Optional wrapper class (e.g. for landing page width) */
  className?: string;
}

const ContactForm = ({ title, className = '' }: ContactFormProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('home');
  const { lang } = useLang();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({ ...defaultFormData });

  const formTitle = title ?? t('contact.form.title');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('contactForm.errors.firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('contactForm.errors.lastNameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('contactForm.errors.emailRequired');
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = t('contactForm.errors.emailInvalid');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('contactForm.errors.phoneRequired');
    } else if (!/^\+?[0-9\s-()]{8,}$/.test(formData.phone)) {
      newErrors.phone = t('contactForm.errors.phoneInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (Object.values(formData).every((val) => val === '')) {
      trackFormStart('contact');
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      toast({
        title: t('contactForm.toasts.validationError.title'),
        description: t('contactForm.toasts.validationError.description'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createKommoLead(formData);
      trackContactFormSubmission('contact');
      trackFormComplete('contact');
      pushDataLayerEvent({
        event: 'form_submit_success',
        form_name: 'contact_form',
      });
      toast({
        title: t('contactForm.toasts.success.title'),
        description: t('contactForm.toasts.success.description'),
      });
      setFormData({ ...defaultFormData });
      setErrors({});
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: t('contactForm.toasts.error.title'),
        description: t('contactForm.toasts.error.description'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact-form-section" className={`bg-white rounded-2xl p-8 shadow-lg border border-dental-gray/10 ${className}`}>
      {formTitle && (
        <h3 className="text-2xl font-display font-bold mb-6 text-dental-dark">{formTitle}</h3>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-dental-dark mb-2">
              {t('contactForm.fieldLabels.firstName')}
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg bg-white border-2 ${errors.firstName ? 'border-red-500' : 'border-dental-gray/30'} focus:outline-none focus:ring-2 focus:ring-dental focus:border-transparent transition shadow-sm`}
              required
            />
            {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-dental-dark mb-2">
              {t('contactForm.fieldLabels.lastName')}
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg bg-white border-2 ${errors.lastName ? 'border-red-500' : 'border-dental-gray/30'} focus:outline-none focus:ring-2 focus:ring-dental focus:border-transparent transition shadow-sm`}
              required
            />
            {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-dental-dark mb-2">
              {t('contactForm.fieldLabels.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg bg-white border-2 ${errors.email ? 'border-red-500' : 'border-dental-gray/30'} focus:outline-none focus:ring-2 focus:ring-dental focus:border-transparent transition shadow-sm`}
              required
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-dental-dark mb-2">
              {t('contactForm.fieldLabels.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg bg-white border-2 ${errors.phone ? 'border-red-500' : 'border-dental-gray/30'} focus:outline-none focus:ring-2 focus:ring-dental focus:border-transparent transition shadow-sm`}
              required
            />
            {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-dental-dark mb-2">
            {t('contactForm.fieldLabels.message')}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={5}
            className="w-full px-4 py-3 rounded-lg bg-white border-2 border-dental-gray/30 focus:outline-none focus:ring-2 focus:ring-dental focus:border-transparent transition shadow-sm"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`btn-shine group w-full flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? t('contactForm.submit.loading') : t('contactForm.submit.idle')}
            <Send size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        <p className="text-xs text-dental-dark/70 text-center">
          {t('contactForm.privacy.prefix')}{' '}
          <a href={buildLangPath(lang, '/privacy-policy')} className="text-dental hover:underline">
            {t('contactForm.privacy.linkLabel')}
          </a>
          {t('contactForm.privacy.suffix')}
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
